// -----------------------------------------------------------------------------
// Chat mentions — the single source of truth for how an @-mention is encoded
// inside a `ChatMessage.Body` string.
//
// A mention is stored inline as a markdown-link-like token so the backend
// schema stays a plain string (no migration) and the text degrades gracefully
// if a client hasn't been updated:
//
//   @[AB12 CDE - Ford Fiesta](vehicle/2b1f9c3a-...)
//   @[John Smith](user/8a4d...)
//
// Both the composer (to paint chips while typing) and the message renderer
// parse the same token, so the encoding only lives here.
// -----------------------------------------------------------------------------

import i18n from "../i18n";
import { SearchPaletteItemType } from "../components/shared/Inputs/SearchPaletteTypes";

// Not a React component, so translate via the i18n instance directly. The
// language selector reloads the page on change, so resolving the folder
// labels once at module load is safe.
const t = (key: string) => i18n.t(key, { ns: "chat" });

export type MentionType = "user" | "vehicle" | "stock" | "order" | "lead";

export interface Mention {
  type: MentionType;
  id: string;
  label: string;
}

/** The selectable "folders" shown when the @ picker first opens — a type must
 * be chosen before any search runs, mirroring the omni search palette. */
export const MENTION_CATEGORIES: { type: MentionType; label: string }[] = [
  { type: "user", label: t("Users") },
  { type: "vehicle", label: t("Vehicles") },
  { type: "stock", label: t("Parts & Stock") },
  { type: "order", label: t("Orders") },
  { type: "lead", label: t("Leads") },
];

export type MentionSegment =
  | { kind: "text"; text: string }
  | { kind: "mention"; mention: Mention };

// Capture group 1 = label, 2 = type, 3 = id. Kept lenient on the id so it
// matches GUIDs regardless of casing.
const MENTION_TOKEN =
  /@\[([^\]]+)\]\((user|vehicle|stock|order|lead)\/([0-9a-fA-F-]+)\)/g;

/** A label can't contain the bracket characters that delimit the token, so we
 * strip them when serialising to keep round-tripping safe. */
export function serializeMention(mention: Mention): string {
  const safeLabel = mention.label.replace(/[[\]()]/g, "").trim();
  return `@[${safeLabel}](${mention.type}/${mention.id})`;
}

/** Splits a stored body into ordered text / mention segments for rendering. */
export function parseMentions(body: string): MentionSegment[] {
  if (!body) return [];

  const segments: MentionSegment[] = [];
  let lastIndex = 0;

  // Reset the shared regex's cursor — it's stateful because of the /g flag.
  MENTION_TOKEN.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = MENTION_TOKEN.exec(body)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ kind: "text", text: body.slice(lastIndex, match.index) });
    }
    segments.push({
      kind: "mention",
      mention: {
        label: match[1],
        type: match[2] as MentionType,
        id: match[3],
      },
    });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < body.length) {
    segments.push({ kind: "text", text: body.slice(lastIndex) });
  }

  return segments;
}

/** Maps our lowercase mention type to the shared palette type so we can reuse
 * `getTypeIcon` / `getTypePillClasses` for consistent iconography + colours. */
export function mentionTypeToPaletteType(type: MentionType): SearchPaletteItemType {
  switch (type) {
    case "vehicle":
      return "Vehicle";
    case "stock":
      return "Stock";
    case "order":
      return "Order";
    case "lead":
      return "Lead";
    case "user":
    default:
      return "User";
  }
}

/** In-app detail route for a mention, or null when the type has no detail page
 * (users are people in the conversation, not a navigable record). Orders are
 * sold parts, so they resolve to the same stock detail view as stock. */
export function mentionDetailUrl(type: MentionType, id: string): string | null {
  switch (type) {
    case "vehicle":
      return `/vehicles/${id}`;
    case "lead":
      return `/leads/${id}`;
    case "stock":
    case "order":
      return `/stock/${id}`;
    case "user":
    default:
      return null;
  }
}
